import { DeviceArch, DeviceKind, DeviceService, DeviceStatus } from '~/generated/graphql';
import { isSmDown } from '~/lib/responsive';
import { formatDeviceKind } from '~/lib/string';
import type { DeviceFiltersState, useDeviceFiltersParams } from './';

export const INITIAL_ARCH = [
  { label: 'arm', selected: false, value: DeviceArch.Arm },
  { label: 'arm64', selected: false, value: DeviceArch.Arm64 },
  { label: 'x64', selected: false, value: DeviceArch.X64 },
];

export const INITIAL_DEMO = [
  { label: 'True', selected: false, value: true },
  { label: 'False', selected: false, value: false },
];

export const INITIAL_DISPLAY_ON = [
  { label: 'On', selected: false, value: true },
  { label: 'Off', selected: false, value: false },
];

export const INITIAL_INTERNAL = [
  { label: 'True', selected: false, value: true },
  { label: 'False', selected: false, value: false },
];

export const INITIAL_KIND = Object.values(DeviceKind)
  .filter((value): value is Exclude<DeviceKind, 'WEB'> => value !== 'WEB')
  .map((value) => ({
    label: formatDeviceKind(value),
    selected: false,
    value,
  }));

export const INITIAL_SERVICES = Object.values(DeviceService).map((value) => ({
  label: value,
  selected: false,
  value,
}));

export const INITIAL_SILENCE = [
  { label: 'True', selected: false, value: true },
  { label: 'False', selected: false, value: false },
];

export const INITIAL_STATUS = [
  { label: 'Online', selected: false, value: DeviceStatus.Healthy },
  { label: 'Offline', selected: false, value: DeviceStatus.Error },
  { label: 'Warning', selected: false, value: DeviceStatus.Warn },
];

export const INITIAL_STORAGE_BELOW = [
  { label: 'Below 10%', selected: false, value: 10 },
  { label: 'Below 25%', selected: false, value: 25 },
  { label: 'Below 50%', selected: false, value: 50 },
];

export const INITIAL_TEMPERATURE_ABOVE = [
  { label: 'Above 150°F (66°C)', selected: false, value: 66 },
  { label: 'Above 160°F (71°C)', selected: false, value: 71 },
  { label: 'Above 170°F (77°C)', selected: false, value: 77 },
];

export const INITIAL_UPTIME_BELOW = [
  { label: 'Within 1 hour', selected: false, value: 1 },
  { label: 'Within 4 hours', selected: false, value: 4 },
  { label: 'Within 12 hours', selected: false, value: 12 },
  { label: 'Within 24 hours', selected: false, value: 24 },
];

export const allUnselectedState: DeviceFiltersState = {
  filters: {
    arch: { open: false, items: INITIAL_ARCH },
    demo: { open: false, items: INITIAL_DEMO },
    displayOn: { open: false, items: INITIAL_DISPLAY_ON },
    displayServers: { open: false, items: [] },
    groups: { open: false, items: [] },
    internal: { open: false, items: INITIAL_INTERNAL },
    kind: { open: false, items: INITIAL_KIND },
    models: { open: false, items: [] },
    playerVersions: { open: false, items: [] },
    services: { open: false, items: INITIAL_SERVICES },
    silence: { open: false, items: INITIAL_SILENCE },
    status: { open: false, items: INITIAL_STATUS },
    storageBelow: { open: false, items: INITIAL_STORAGE_BELOW },
    tags: { open: false, items: [] },
    temperatureAbove: { open: false, items: INITIAL_TEMPERATURE_ABOVE },
    tvBrands: { open: false, items: [] },
    uptimeBelow: { open: false, items: INITIAL_UPTIME_BELOW },
  },
  panel: { open: false },
};

export const getDeviceFiltersState = (
  params: ReturnType<typeof useDeviceFiltersParams> | undefined,
) => {
  if (!params) return allUnselectedState;
  const isDeviceSmDown = isSmDown();
  return {
    filters: {
      arch: {
        open: params.arch.length > 0,
        items: INITIAL_ARCH.map((x) => ({ ...x, selected: params.arch.includes(x.value) })),
      },
      demo: {
        open: params.demo.length > 0,
        items: INITIAL_DEMO.map((x) => ({ ...x, selected: params.demo.includes(x.value) })),
      },
      displayOn: {
        open: params.displayOn.length > 0,
        items: INITIAL_DISPLAY_ON.map((x) => ({
          ...x,
          selected: params.displayOn.includes(x.value),
        })),
      },
      displayServers: { open: false, items: [] },
      groups: { open: false, items: [] },
      internal: {
        open: params.internal.length > 0,
        items: INITIAL_INTERNAL.map((x) => ({
          ...x,
          selected: params.internal.includes(x.value),
        })),
      },
      kind: {
        open: params.kind.length > 0,
        items: INITIAL_KIND.map((x) => ({ ...x, selected: params.kind.includes(x.value) })),
      },
      models: { open: false, items: [] },
      playerVersions: { open: false, items: [] },
      services: {
        open: params.services.length > 0,
        items: INITIAL_SERVICES.map((x) => ({ ...x, selected: params.services.includes(x.value) })),
      },
      silence: {
        open: params.silence.length > 0,
        items: INITIAL_SILENCE.map((x) => ({ ...x, selected: params.silence.includes(x.value) })),
      },
      status: {
        open: params.status.length > 0,
        items: INITIAL_STATUS.map((x) => ({ ...x, selected: params.status.includes(x.value) })),
      },
      storageBelow: {
        open: params.storageBelow != null,
        items: INITIAL_STORAGE_BELOW.map((x) => ({
          ...x,
          selected: x.value === params.storageBelow,
        })),
      },
      tags: { open: false, items: [] },
      temperatureAbove: {
        open: params.temperatureAbove != null,
        items: INITIAL_TEMPERATURE_ABOVE.map((x) => ({
          ...x,
          selected: x.value === params.temperatureAbove,
        })),
      },
      tvBrands: { open: false, items: [] },
      uptimeBelow: {
        open: params.uptimeBelow != null,
        items: INITIAL_UPTIME_BELOW.map((x) => ({
          ...x,
          selected: x.value === params.uptimeBelow,
        })),
      },
    },
    panel: {
      open:
        !isDeviceSmDown &&
        Object.values(params).some((v) => (Array.isArray(v) ? v.length > 0 : v != null)),
    },
  };
};
